import React, { useState, useCallback, useEffect, useRef } from 'react';

import { Box, Flex, Text, useToast, Skeleton } from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { parseISO, format } from 'date-fns';
import * as Yup from 'yup';

import { User } from '~/modules/accessControl/@types/user';
import { ActivityProps } from '~/modules/activityExecution/@types/activity';
import { UserSelect } from '~/modules/management/pages/Department';
import api from '~/shared/services/api';
import getOptionsAllowedResponsibles from '~/utils/getOptionsAllowedResponsibles';
import getValidationErrors from '~/utils/getValidationErrors';

import InputChakra from '../../InputChakra';
import SelectChakra, { Option } from '../../InputChakra/SelectChakra';
import ModalChakra from '../ChakraUI';

interface ModalReplanProps {
  planning: ActivityProps;
  isOpen: boolean;
  onClose: () => void;
  updated: () => void;
}

interface ReplanData {
  activity_id: string;
  attendance_id: string;
  current_employee: string;
  duration_activities: string;
  employee_attendance_id: string;
  fatal_date: string;
  fatal_hours: string;
  minimum_start: string;
  new_employee: string;
  status_id: string;
}

const ModalReplan: React.FC<ModalReplanProps> = ({
  planning,
  isOpen,
  onClose,
  updated,
}) => {
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [usersOptions, setUsersOptions] = useState<Option[]>([]);
  const addToast = useToast();

  useEffect(() => {
    api
      .get<User[]>('users/all')
      .then((response) => {
        const usersData = response.data;

        /* console.log(
          getOptionsAllowedResponsibles(
            planning?.allowed_responsibilities,
            usersData,
          ),
        ); */

        setUsersOptions(
          getOptionsAllowedResponsibles(
            planning?.allowed_responsibilities,
            usersData,
          ),
        );

        return response.data;
      })
      .finally(() => setLoading(false));
  }, [planning]);

  const handleSubmitReplan = useCallback(
    async (dataForm: ReplanData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          duration_activities: Yup.string().required('Campo obrigatório'),
        });

        await schema.validate(dataForm, {
          abortEarly: false,
        });
      } catch (err) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);

        return;
      }

      setLoadingSubmit(true);

      api
        .put(`plannings/${dataForm.employee_attendance_id}`, dataForm)

        .then(() => {
          addToast({
            position: 'top-right',
            title: 'Atividade replanejada',
            description: 'Atividade replanejada com sucesso!',
            status: 'success',
            isClosable: true,
          });

          // Avisar ao componente pai que os dados foram atualizados
          updated();
        })
        .catch(() => {
          addToast({
            position: 'top-right',
            status: 'error',
            title: 'Não foi possível replanejar a atividade.',
            isClosable: true,
          });
        })
        .finally(() => {
          setLoadingSubmit(false);
          onClose();
        });
    },
    [addToast, onClose, updated],
  );

  return (
    <ModalChakra
      title="Replanejar Atividade"
      onClose={() => {
        onClose();
      }}
      onSubmit={() => formRef.current?.submitForm()}
      isOpen={isOpen}
      titleButtonCancel="Cancelar"
      isLoading={loadingSubmit}
    >
      {planning && (
        <>
          <Form
            onSubmit={handleSubmitReplan}
            ref={formRef}
            initialData={{
              new_employee: planning.employee_id,
              attendance_id: planning.attendance_id,
              minimum_start: planning.minimum_start,
              fatal_date: planning.fatal_date,
              fatal_hours: planning.fatal_time
                ? format(
                    parseISO(`${planning.fatal_date} ${planning.fatal_time}`),
                    'HH:mm',
                  )
                : '',
              duration_activities: format(
                parseISO(`${planning.start_date} ${planning.duration}`),
                'HH:mm',
              ),
              current_employee: planning.employee_id,
              status_id: planning.situation_id,
              activity_id: planning.activity_id,
              employee_attendance_id: planning.id,
            }}
          >
            <Box key={planning.order_activity}>
              <Text color="#89889c">Atividade</Text>

              <Text mb="30px" fontSize="2xl">
                {`${planning.order_activity} - ${planning.name}`}
              </Text>

              {loading ? (
                <Box mb={4}>
                  <Skeleton height="20px" mb={1} width="250px" />
                  <Skeleton height="48px" width="300px" />
                </Box>
              ) : (
                <SelectChakra
                  label="Selecionar Funcionário"
                  options={usersOptions}
                  name="new_employee"
                />
              )}

              <InputChakra
                type="datetime-local"
                label="Data e hora início mínimo"
                name="minimum_start"
                width="auto"
              />
              <Text color="#718096">Data e hora fatal</Text>
              <Flex alignItems="center">
                <Box mr="20px">
                  <InputChakra type="date" name="fatal_date" width="auto" />
                </Box>

                <Box>
                  <InputChakra type="time" name="fatal_hours" width="auto" />
                </Box>
              </Flex>

              <InputChakra
                type="time"
                label="Duração da ativade"
                name="duration_activities"
                width="auto"
              />

              <InputChakra type="hidden" name="current_employee" />
              <InputChakra type="hidden" name="status_id" />
              <InputChakra type="hidden" name="activity_id" />
              <InputChakra type="hidden" name="attendance_id" />
              <InputChakra type="hidden" name="employee_attendance_id" />
            </Box>
          </Form>
        </>
      )}
    </ModalChakra>
  );
};

export default ModalReplan;
